import { addReferenceCitationToComponent } from 'modules/References/services/addReferenceCitationToComponent';
import { updateReferenceCitationsIds } from 'modules/References/utils/updateReferenceCitationsIds';
import { call } from 'redux-saga/effects';
import * as Models from 'models';
import { handleSagaError } from 'services/handleError';
import { createLexicalTextEditor } from '../editors';
import { $getReferenceCitationNodes } from '../Plugins/ReferenceCitationPlugin';
import { $replaceReferenceCitationId } from '../Plugins/ReferenceCitationPlugin/utils/replaceReferenceCitationId';

type Result = {
  newReferenceCitationDocuments: Models.ReferenceCitationMap[];
  updatedTextComponent: Models.TextComponentMap;
};

export function* processReferenceCitations(
  textComponent: Models.TextComponentMap,
): Generator<unknown, Result | undefined, ReturnTypeSaga<typeof addReferenceCitationToComponent>> {
  try {
    const lexicalState = textComponent.get('lexicalState');
    if (!lexicalState) {
      return {
        updatedTextComponent: updateReferenceCitationsIds(textComponent, []),
        newReferenceCitationDocuments: [],
      };
    }

    const editor = createLexicalTextEditor(lexicalState);
    const referencesIds = editor.read(() => $getReferenceCitationNodes().map(node => node.getReferenceId()));
    if (referencesIds.length === 0) {
      return {
        updatedTextComponent: updateReferenceCitationsIds(textComponent, []),
        newReferenceCitationDocuments: [],
      };
    }

    const result: Result = {
      updatedTextComponent: textComponent,
      newReferenceCitationDocuments: [],
    };
    for (const referenceId of referencesIds) {
      const {
        newReferenceCitationDocument,
        newReferenceCitationId,
      } = yield call(addReferenceCitationToComponent, referenceId);

      if (newReferenceCitationId) {
        editor.update(
          () => $replaceReferenceCitationId(referenceId, newReferenceCitationId),
          { discrete: true },
        );
      }

      if (newReferenceCitationDocument) {
        result.newReferenceCitationDocuments.push(newReferenceCitationDocument);
      }
    }

    const updatedReferencesIds = editor.read(() => $getReferenceCitationNodes().map(node => node.getReferenceId()));

    result.updatedTextComponent = updateReferenceCitationsIds(
      textComponent.set('lexicalState', JSON.stringify(editor.getEditorState())),
      updatedReferencesIds,
    );

    return result;
  } catch (error) {
    yield call(handleSagaError, error, 'Lexical.processReferenceCitations', 'ProcessReferenceCitations');

    return undefined;
  }
}
